import axiosConfig from "./axiosConfig";

// Cache utilities
const CACHE_DURATION = {
  SHORT: 5 * 60 * 1000, // 5 minutes
  MEDIUM: 30 * 60 * 1000, // 30 minutes
  LONG: 24 * 60 * 60 * 1000, // 1 day
};

// Simple in-memory cache
const memoryCache = new Map();

// Function to check if cache is valid
const isCacheValid = (key) => {
  if (!memoryCache.has(key)) return false;
  const { timestamp, expiry } = memoryCache.get(key);
  return Date.now() - timestamp < expiry;
};

// Function to get from cache
const getFromCache = (key) => {
  if (!isCacheValid(key)) return null;
  return memoryCache.get(key).data;
};

// Function to set cache
const setCache = (key, data, expiry = CACHE_DURATION.MEDIUM) => {
  memoryCache.set(key, {
    data,
    timestamp: Date.now(),
    expiry,
  });
};


// Function to clear cache for a pattern
const clearCachePattern = (pattern) => {
  for (const key of memoryCache.keys()) {
    if (key.includes(pattern)) {
      memoryCache.delete(key);
    }
  }
};

// Enhanced axios config with caching headers
const configWithCache = {
  headers: {
    'Cache-Control': 'max-age=300', // 5 minutes browser cache
  }
};

// Categories
export const getCategories = async () => {
  const cacheKey = 'categories';
  
  // Try to get from cache first
  const cachedData = getFromCache(cacheKey);
  if (cachedData) {
    return cachedData;
  }
  
  try {
    const response = await axiosConfig.get("/uploads/category", configWithCache);
    // Store in cache
    setCache(cacheKey, response.data, CACHE_DURATION.MEDIUM);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la recherche des categories: ', error);
    throw error;
  }
};

// Course By category
export const getCourseByCategory = async (encodedCategory) => {
  const cacheKey = `courseByCategory_${encodedCategory}`;
  
  // Try to get from cache first
  const cachedData = getFromCache(cacheKey);
  if (cachedData) {
    return cachedData;
  }
  
  try {
    const response = await axiosConfig.get(`/uploads/category/${encodedCategory}`, configWithCache);
    // Store in cache
    setCache(cacheKey, response.data, CACHE_DURATION.SHORT);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la recherche des categories: ', error);
    throw error;
  }
};

// Courses
export const getAllCourses = async () => {
  const cacheKey = 'allCourses';
  
  // Try to get from cache first
  const cachedData = getFromCache(cacheKey);
  if (cachedData) {
    return cachedData;
  }
  
  try {
    const response = await axiosConfig.get("/uploads/all", configWithCache);
    // Store in cache
    setCache(cacheKey, response.data, CACHE_DURATION.SHORT);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la recherche des cours: ', error);
    throw error;
  }
};

export const getLimitedCourses = async (limit) => {
  const cacheKey = `limitedCourses_${limit}`;
  
  // Try to get from cache first
  const cachedData = getFromCache(cacheKey);
  if (cachedData) {
    return cachedData;
  }
  
  try {
    const response = await axiosConfig.get(`/uploads/favoris?limit=${limit}`, configWithCache);
    // Store in cache
    setCache(cacheKey, response.data, CACHE_DURATION.SHORT);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des cours limités', error);
    throw error;
  }
};

export const getCourseById = async (courseId) => {
  const cacheKey = `course_${courseId}`;
  
  // Try to get from cache first
  const cachedData = getFromCache(cacheKey);
  if (cachedData) {
    return cachedData;
  }
  
  try {
    const response = await axiosConfig.get(`/uploads/course/${courseId}`, configWithCache);
    // Store in cache
    setCache(cacheKey, response.data, CACHE_DURATION.MEDIUM);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la recherche du cours: ', error);
    throw error;
  }
};

// Files - special handling for blob data
export const fetchFile = async (fileName, fileType) => {
  const cacheKey = `file_${fileType}_${fileName}`;
  
  // For files, we'll use the browser's Cache API which works better with blobs
  try {
    // Try to get from cache first
    const cachedData = getFromCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const response = await axiosConfig.get(
      `/uploads/download/${encodeURIComponent(fileType)}/${encodeURIComponent(fileName)}`, 
      {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'max-age=86400', // 24 hours for files
        }
      }
    );

    if (response.status !== 200) {
      throw new Error("Erreur lors du téléchargement du fichier");
    }

    // Conversion en URL Blob
    const fileUrl = URL.createObjectURL(response.data);
    
    // Store URL in cache
    setCache(cacheKey, fileUrl, CACHE_DURATION.LONG);
    
    return fileUrl;
  } catch (error) {
    console.error("Erreur:", error);
    return null;
  }
};

// Functions that modify data - should invalidate cache
export const toggleFavorisStatus = async (courseId) => {
  try {
    const response = await axiosConfig.post(`/uploads/toggleFavoris/${courseId}`);
    
    // Clear related caches after a mutation
    clearCachePattern('favoris');
    clearCachePattern(`course_${courseId}`);
    
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du statut des favoris: ', error);
    throw error;
  }
};

export const getFavoris = async () => {
  const cacheKey = 'favoris';
  
  // Try to get from cache first
  const cachedData = getFromCache(cacheKey);
  if (cachedData) {
    return cachedData;
  }
  
  try {
    const response = await axiosConfig.get("/uploads/favoris", configWithCache);
    // Store in cache
    setCache(cacheKey, response.data, CACHE_DURATION.SHORT);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la recherche des favoris: ', error);
    throw error;
  }
};

export const getFavorisById = async (courseId) => {
  const cacheKey = `favorisById_${courseId}`;
  
  // Try to get from cache first
  const cachedData = getFromCache(cacheKey);
  if (cachedData) {
    return cachedData;
  }
  
  try {
    const response = await axiosConfig.get(`/uploads/favoris/${courseId}`, configWithCache);
    // Store in cache
    setCache(cacheKey, response.data, CACHE_DURATION.SHORT);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la recherche des favoris: ', error);
    throw error;
  }
};

// Function to clear all caches - can be called when user logs out or on demand
export const clearAllCaches = () => {
  memoryCache.clear();
};